import React from 'react';
import { API_BASE, toUrl } from '../lib/config';
type MediaItem = {
  id: string;
  name: string | null;
  url: string;
  filename: string | null;
  mime: string | null;
  sizeBytes: number | null;
  isActive: boolean;
  createdAt: string;
};

const MediaYonetim: React.FC = () => {
  const [file, setFile] = React.useState<File | null>(null);
  const [name, setName] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [items, setItems] = React.useState<MediaItem[]>([]);
  const [error, setError] = React.useState<string>('');
  const [preview, setPreview] = React.useState<string>('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [deletingId, setDeletingId] = React.useState<string>('');
  const [q, setQ] = React.useState<string>('');
  const [editingId, setEditingId] = React.useState<string>('');
  const [editName, setEditName] = React.useState<string>('');
  const [editFile, setEditFile] = React.useState<File | null>(null);
  const [editPreview, setEditPreview] = React.useState<string>('');
  const [editSaving, setEditSaving] = React.useState<boolean>(false);
  const [confirmId, setConfirmId] = React.useState<string>('');
  const [confirmLoading, setConfirmLoading] = React.useState<boolean>(false);

  const fetchItems = React.useCallback(async () => {
    try {
      setError('');
      const url = q ? `/api/media/admin?q=${encodeURIComponent(q)}` : '/api/media/admin';
      const res = await fetch(toUrl(url));
      if (!res.ok) throw new Error('Liste alınamadı');
      const data = await res.json();
      setItems(data || []);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    }
  }, [q]);

  React.useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Debounce search input
  React.useEffect(() => {
    const t = setTimeout(() => {
      fetchItems();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Preview URL lifecycle
  React.useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview('');
  }, [file]);

  // Edit preview lifecycle
  React.useEffect(() => {
    if (editFile) {
      const url = URL.createObjectURL(editFile);
      setEditPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setEditPreview('');
  }, [editFile]);

  const onDelete = (id: string) => {
    setConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!confirmId) return;
    try {
      setConfirmLoading(true);
      setDeletingId(confirmId);
      const res = await fetch(toUrl(`/api/media/admin/${confirmId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Silme işlemi başarısız');
      setConfirmId('');
      await fetchItems();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setConfirmLoading(false);
      setDeletingId('');
    }
  };

  const openEdit = (m: MediaItem) => {
    setEditingId(m.id);
    setEditName(m.name || m.filename || '');
    setEditFile(null);
    setEditPreview('');
  };

  const cancelEdit = () => {
    setEditingId('');
    setEditName('');
    setEditFile(null);
    setEditPreview('');
  };

  const onEditSave = async (id: string) => {
    if (!editName.trim()) {
      setError('Lütfen görsel için bir isim girin');
      return;
    }
    try {
      setEditSaving(true);
      setError('');
      const fd = new FormData();
      fd.append('name', editName.trim());
      if (editFile) fd.append('file', editFile);
      const res = await fetch(toUrl(`/api/media/admin/${id}`), {
        method: 'PUT',
        body: fd,
      });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      cancelEdit();
      await fetchItems();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setEditSaving(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Lütfen bir görsel seçin');
      return;
    }
    if (!name.trim()) {
      setError('Lütfen görsel için bir isim girin');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name.trim());
      const res = await fetch(toUrl('/api/media/admin'), {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Yükleme başarısız');
      }
      // Başarılı ise formu sıfırla ve listeyi yenile
      setFile(null);
      setName('');
      await fetchItems();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <h2 style={{ marginBottom: 16 }}>Medya (Media)</h2>

      <div className="card" style={{ padding: 16, marginBottom: 24 }}>
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>Yeni Medya Yükle</h3>
        <form onSubmit={onSubmit}>
          {/* Dropzone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) setFile(f);
            }}
            style={{
              border: '2px dashed #cbd5e1',
              borderRadius: 12,
              padding: 16,
              display: 'grid',
              gridTemplateColumns: '160px 1fr',
              gap: 16,
              cursor: 'pointer',
              alignItems: 'center',
              background: '#f8fafc',
            }}
          >
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
              width: 160,
              height: 120,
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            >
              {preview ? (
                <img src={preview} alt="Önizleme" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (
                <div style={{ textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                  Görsel seçin ya da buraya sürükleyip bırakın
                  <div style={{ fontSize: 11 }}>PNG, JPG, SVG desteklenir</div>
                </div>
              )}
            </div>
            <div>
              <div style={{ marginBottom: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="btn" onClick={() => fileInputRef.current?.click()} disabled={loading}>Dosya Seç</button>
                {file && (
                  <button type="button" className="btn" onClick={() => setFile(null)} disabled={loading}>Temizle</button>
                )}
              </div>
              {file && (
                <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
                  Seçilen: {file.name} ({Math.round(file.size / 1024)} KB)
                </div>
              )}
              <label className="form-label" style={{ display: 'block' }}>İsim (zorunlu)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Örn: Banner - Ana Sayfa"
                className="input"
                style={{ width: '100%', maxWidth: 420 }}
                required
              />
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
            style={{ display: 'none' }}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={loading || !file || !name.trim()}>
              {loading ? 'Yükleniyor...' : 'Yükle'}
            </button>
            <button type="button" className="btn" onClick={() => { setFile(null); setName(''); }} disabled={loading}>Formu Temizle</button>
          </div>
        </form>
        {error && (
          <div style={{ color: '#b00020', marginTop: 8 }}>{error}</div>
        )}
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Yüklenmiş Medyalar</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Ara: isim, dosya adı veya url"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="input"
              style={{ width: 260 }}
            />
            {q && (
              <button className="btn" onClick={() => setQ('')}>Temizle</button>
            )}
            <button className="btn" onClick={fetchItems}>Yenile</button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginTop: 16 }}>
          {items.map((m) => (
            <div key={m.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
              <div style={{
                width: '100%',
                height: 200,
                background: '#fff',
                borderRadius: 6,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}>
                {/* public url backend tarafından /api/media/:id şeklinde dönüyor */}
                <img
                  src={toUrl(m.url)}
                  alt={m.name || m.filename || m.id}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  crossOrigin="anonymous"
                  onError={(e) => {
                    const el = e.currentTarget as HTMLImageElement;
                    // Fallback: if prefixed URL failed, try raw; if raw fails, try prefixed
                    const current = el.getAttribute('src') || '';
                    const raw = m.url;
                    const pref = toUrl(m.url);
                    const next = current === pref ? raw : pref;
                    if (current !== next) el.src = next;
                  }}
                />
                {/* Hover overlay actions */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.45)',
                    color: '#fff',
                    opacity: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    transition: 'opacity 0.15s ease-in-out',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '0')}
                >
                  <button
                    type="button"
                    onClick={() => openEdit(m)}
                    className="btn"
                    style={{
                      background: '#2563eb',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                    title="Düzenle"
                  >
                    {/* Pencil icon */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M16.5 3.5l4 4L8 20H4v-4L16.5 3.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Düzenle
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(m.id)}
                    className="btn"
                    style={{
                      background: '#dc2626',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                    disabled={deletingId === m.id}
                    title="Sil"
                  >
                    {/* Trash icon (inline) */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    {deletingId === m.id ? 'Siliniyor...' : 'Sil'}
                  </button>
                </div>
              </div>
              <div style={{ marginTop: 8, fontSize: 13, color: '#111827' }}>
                {m.name || m.filename || '(İsimsiz)'}
              </div>
              {/* Edit modal moved to root; inline panel removed */}
            </div>
          ))}
          {items.length === 0 && (
            <div style={{ gridColumn: '1/-1', color: '#6b7280' }}>Henüz medya yüklenmemiş.</div>
          )}
        </div>
      </div>
      {/* Edit Modal */}
      {editingId && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={(e) => {
            // backdrop click closes
            if (e.target === e.currentTarget) cancelEdit();
          }}
        >
          <div className="card" style={{ width: 'min(800px, 96vw)', padding: 0, background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Medyayı Düzenle</h3>
              <button type="button" className="btn" onClick={cancelEdit} aria-label="Kapat">Kapat</button>
            </div>
            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16 }}>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#fafafa' }}>
                {editPreview ? (
                  <img src={editPreview} alt="Önizleme" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                ) : (
                  <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center' }}>Yeni görsel seçilmedi</div>
                )}
              </div>
              <div>
                <div style={{ marginBottom: 12 }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: 4 }}>İsim</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="input"
                    style={{ width: '100%', maxWidth: 480 }}
                    required
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: 4 }}>Yeni Görsel (opsiyonel)</label>
                  <input type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml" onChange={(e) => setEditFile(e.target.files?.[0] || null)} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn" onClick={cancelEdit} disabled={editSaving}>İptal</button>
                  <button type="button" className="btn btn-primary" onClick={() => onEditSave(editingId)} disabled={editSaving || !editName.trim()}>
                    {editSaving ? 'Kaydediliyor...' : 'Kaydet'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {confirmId && (
        <div
          role="dialog"
          aria-modal="true"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmId(''); }}
        >
          <div className="card" style={{ width: 420, background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Silme Onayı</h3>
            </div>
            <div style={{ padding: 16 }}>
              Bu medyayı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={() => setConfirmId('')} disabled={confirmLoading}>İptal</button>
              <button type="button" className="btn btn-primary" style={{ background: '#dc2626' }} onClick={confirmDelete} disabled={confirmLoading}>
                {confirmLoading ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaYonetim;
